import { z } from "zod/v4";

import {
  type SubscriptionBillingProvider,
  SubscriptionBillingProviderList,
  type SubscriptionCollectionMethod,
  SubscriptionCollectionMethodList,
} from "../../subscriptions";

import {
  type BillingCadence,
  BillingCadenceList,
  type BillingCommandFamily,
  BillingCommandFamilyList,
  type BillingEffectiveTiming,
  BillingEffectiveTimingList,
  type BillingScheduledBoundaryReason,
  BillingScheduledBoundaryReasonList,
  type BillingScheduledFollowupCommandType,
  BillingScheduledFollowupCommandTypeList,
} from "./catalog";
import { BILLING_COMMAND_RUNTIME_CONTRACT } from "./graph";
import {
  type BillingPlanDeferredReason,
  BillingPlanDeferredReasonSchema,
  type BillingPlanIntentRefV1,
  BillingPlanIntentRefV1Schema,
} from "./plan-intent-ref";
import {
  type AgencyLifecyclePayload,
  AgencyLifecyclePayloadSchema,
  type BillingFollowupPayload,
  BillingFollowupPayloadSchema,
  BillingRegimeConversionContextSchema,
  type BillingReleaseScheduledWakeupV1,
  BillingReleaseScheduledWakeupV1Schema,
  type BillingScheduledWakeupV1,
  BillingScheduledWakeupV1Schema,
  type ProviderLifecycleAction,
  ProviderLifecycleActionSchema,
} from "./payloads";

/**
 * Current revision of the delayed-wakeup ownership contract. Stamped onto every
 * checkpoint the runtime writes, so a checkpoint without it is known to predate
 * the contract rather than merely to have booked nothing.
 */
export const BILLING_WAKEUP_OWNERSHIP_CONTRACT_VERSION = 1;

/**
 * How releasing a booked boundary settled. `already_fired` means the booking's
 * follow-up had already decided, so the release stopped nothing.
 */
export const BillingWakeupReleaseOutcomeList = [
  "released",
  "already_released",
  "already_fired",
] as const;

export type BillingWakeupReleaseOutcome =
  (typeof BillingWakeupReleaseOutcomeList)[number];

/**
 * Upper bound on retained booking ids. A command books a boundary or two in
 * practice; the cap only stops a repair loop that re-books under fresh keys
 * from growing the checkpoint without limit. Oldest entries drop first.
 */
export const BILLING_MAX_BOOKED_WAKEUP_JOB_IDS = 32;

export const BillingRuntimeCheckpointPhaseList = [
  "ingress_routing_task",
  "projection_sync_task_orphan_enqueued",
  "projection_sync_task_for_routed",
  "command_dispatcher_task",
  "starting",
  "intent_normalized",
  "policy_fact_loaded",
  "policy_applied",
  "execution_plan_ready",
  "stripe_subscription_link_created",
  "awaiting_webhook",
  "awaiting_shopify",
  "awaiting_scheduled_wakeup",
  "internal_actions_applied",
  "provider_execution_finalizing",
  "provider_refs_resolved",
  "provider_resume_token_cleared",
  "projection_applied",
  "downstream_effects_applied",
  "timer_router_applied",
  "reconciliation_authoritative_loaded",
  "reconciliation_drift_evaluated",
  "reconciliation_repair_planned",
  "repair_sweep_running",
  "repair_sweep_completed",
  "shopify_cap_approval_completed",
  "operator_cancelled",
  "finalizing",
  "failed",
] as const;

export type BillingRuntimeCheckpointPhase =
  (typeof BillingRuntimeCheckpointPhaseList)[number];

/**
 * ASL state names of the ingress, parent runtime, and provider-execution-child
 * Step Functions graphs that drive billing commands. Mirrors the IDs declared
 * in `ig-sst/eng-team/be-stacks/src/state-machines/billing-command-{ingress,runtime}{,-graph}.ts`.
 *
 * Used by the workflow trace to attribute persisted rows (status signals,
 * Stripe correlations, downstream effects) to the ASL state that produced
 * them, so the trace UI can filter the per-step evidence panel by node.
 */
export const BillingRuntimeAslStateName = {
  IngressRoutingTask: "IngressRoutingTask",
  ProjectionSyncTaskOrphanEnqueued: "ProjectionSyncTaskOrphanEnqueued",
  ProjectionSyncTaskForRouted: "ProjectionSyncTaskForRouted",
  ProjectionSyncCheckpointTaskOrphanEnqueued:
    "ProjectionSyncCheckpointTaskOrphanEnqueued",
  ProjectionSyncCheckpointTaskForRouted:
    "ProjectionSyncCheckpointTaskForRouted",
  CommandDispatcherTask: "CommandDispatcherTask",
  RuntimeStartTask: "RuntimeStartTask",
  IntentSelectionTask: "IntentSelectionTask",
  PolicyFactLoadingTask: "PolicyFactLoadingTask",
  PolicyApplicationTask: "PolicyApplicationTask",
  PolicyPlanAssemblyTask: "PolicyPlanAssemblyTask",
  InternalSchedulingTask: "InternalSchedulingTask",
  ProjectionEntitlementsTask: "ProjectionEntitlementsTask",
  DownstreamEffectsTask: "DownstreamEffectsTask",
  TimerRouterTask: "TimerRouterTask",
  TimerFollowupSchedulingTask: "TimerFollowupSchedulingTask",
  ReconciliationTask: "ReconciliationTask",
  RepairSweepTask: "RepairSweepTask",
  UpdateCommandStatusTask: "UpdateCommandStatusTask",
  StripePreflightTask: "StripePreflightTask",
  StripeMutationTask: "StripeMutationTask",
  StripeCustomerReadyWaitTask: "StripeCustomerReadyWaitTask",
  StripeDelayedCreateTask: "StripeDelayedCreateTask",
  StripeFinalizeTask: "StripeFinalizeTask",
  ShopifyMutationTask: "ShopifyMutationTask",
  ShopifyFinalizeTask: "ShopifyFinalizeTask",
  ShopifyCapApprovalTask: "ShopifyCapApprovalTask",
  ProviderOutcomeGateTask: "ProviderOutcomeGateTask",
} as const;

export type BillingRuntimeAslStateName =
  (typeof BillingRuntimeAslStateName)[keyof typeof BillingRuntimeAslStateName];

/**
 * Maps each persisted checkpoint phase to the ASL state that emits it.
 * `finalizing` is retained as a legacy checkpoint phase for historical traces.
 */
export const BillingRuntimeCheckpointPhaseToAslStateName: Record<
  BillingRuntimeCheckpointPhase,
  BillingRuntimeAslStateName
> = {
  ingress_routing_task: BillingRuntimeAslStateName.IngressRoutingTask,
  projection_sync_task_orphan_enqueued:
    BillingRuntimeAslStateName.ProjectionSyncCheckpointTaskOrphanEnqueued,
  projection_sync_task_for_routed:
    BillingRuntimeAslStateName.ProjectionSyncCheckpointTaskForRouted,
  command_dispatcher_task: BillingRuntimeAslStateName.CommandDispatcherTask,
  starting: BillingRuntimeAslStateName.RuntimeStartTask,
  intent_normalized: BillingRuntimeAslStateName.IntentSelectionTask,
  policy_fact_loaded: BillingRuntimeAslStateName.PolicyFactLoadingTask,
  policy_applied: BillingRuntimeAslStateName.PolicyApplicationTask,
  execution_plan_ready: BillingRuntimeAslStateName.PolicyPlanAssemblyTask,
  stripe_subscription_link_created:
    BillingRuntimeAslStateName.StripeMutationTask,
  awaiting_webhook: BillingRuntimeAslStateName.StripeMutationTask,
  awaiting_shopify: BillingRuntimeAslStateName.ShopifyMutationTask,
  awaiting_scheduled_wakeup: BillingRuntimeAslStateName.InternalSchedulingTask,
  internal_actions_applied: BillingRuntimeAslStateName.InternalSchedulingTask,
  provider_execution_finalizing: BillingRuntimeAslStateName.StripeFinalizeTask,
  provider_refs_resolved: BillingRuntimeAslStateName.StripeFinalizeTask,
  provider_resume_token_cleared: BillingRuntimeAslStateName.StripeFinalizeTask,
  projection_applied: BillingRuntimeAslStateName.ProjectionEntitlementsTask,
  downstream_effects_applied: BillingRuntimeAslStateName.DownstreamEffectsTask,
  timer_router_applied: BillingRuntimeAslStateName.TimerFollowupSchedulingTask,
  reconciliation_authoritative_loaded:
    BillingRuntimeAslStateName.ReconciliationTask,
  reconciliation_drift_evaluated: BillingRuntimeAslStateName.ReconciliationTask,
  reconciliation_repair_planned: BillingRuntimeAslStateName.ReconciliationTask,
  repair_sweep_running: BillingRuntimeAslStateName.RepairSweepTask,
  repair_sweep_completed: BillingRuntimeAslStateName.RepairSweepTask,
  shopify_cap_approval_completed:
    BillingRuntimeAslStateName.ShopifyCapApprovalTask,
  operator_cancelled: BillingRuntimeAslStateName.CommandDispatcherTask,
  finalizing: BillingRuntimeAslStateName.StripeFinalizeTask,
  failed: BillingRuntimeAslStateName.UpdateCommandStatusTask,
};

export const BillingAcceptedSignalSourceList = [
  "webhook",
  "scheduler",
] as const;

export type BillingAcceptedSignalSource =
  (typeof BillingAcceptedSignalSourceList)[number];

export const BILLING_WORKFLOW_NON_RETRYABLE_TASK_ERROR_NAME =
  "BillingWorkflowNonRetryableTaskError";

export const BILLING_WORKFLOW_RETRYABLE_TASK_ERROR_NAME =
  "BillingWorkflowRetryableTaskError";

/**
 * Where a runtime failure was raised, as stamped by the catching ASL Pass and
 * replayed to the terminal-status resolver. The distinction is a safety
 * contract, not a label: only `provider_execution` proves the child SM ran, so
 * only it may terminate a command as `rejected` on a bare task failure.
 * `provider_execution_start` means the StartExecution service call itself
 * faulted, so no provider mutation was ever attempted.
 */
export const BillingRuntimeFailureSourceList = [
  "parent_runtime_task",
  "provider_execution_start",
  "provider_execution",
] as const;

export type BillingRuntimeFailureSource =
  (typeof BillingRuntimeFailureSourceList)[number];

/**
 * The ASL error names the parent graph routes off the provider lane while
 * starting the provider-execution child. `States.TaskFailed` is a wildcard
 * that swallows these unless they are retried and caught ahead of it, so the
 * parent graph and the terminal-status resolver share this list.
 *
 * The two names are not equally informative, and the difference is why
 * `isRetryableBillingProviderExecutionStartFault` exists rather than plain
 * list membership:
 *
 * - `StepFunctions.ExecutionLimitExceeded` is the StartExecution quota
 *   rejection. It is specific, it can only precede execution creation, and AWS
 *   documents retrying it by name for exactly this nested-workflow case.
 * - `StepFunctions.AWSStepFunctionsException` is the generic Step Functions
 *   service-exception bucket, and it is the name a StartExecution rate limit
 *   actually surfaces under — the throttle identifies itself only in the
 *   `Cause` ("Rate exceeded ... Error Code: ThrottlingException"). There is no
 *   `StepFunctions.ThrottlingException` error name to match on.
 *
 * Retrying the generic name in place is safe: the retry re-sends the state
 * input verbatim, so the plan's idempotency keys are unchanged. Deciding a
 * command's terminal status on it is not, so that decision demands the
 * throttle evidence below and otherwise stays on the rejected lane.
 */
export const BILLING_PROVIDER_EXECUTION_START_FAULT_ERROR_NAMES = [
  "StepFunctions.ExecutionLimitExceeded",
  "StepFunctions.AWSStepFunctionsException",
] as const;

export type BillingProviderExecutionStartFaultErrorName =
  (typeof BILLING_PROVIDER_EXECUTION_START_FAULT_ERROR_NAMES)[number];

/** The API error code a throttled StartExecution reports inside `Cause`. */
const PROVIDER_EXECUTION_START_THROTTLE_CAUSE_CODE = "ThrottlingException";

/**
 * Whether a start-lane failure proves the child never started AND can succeed
 * on a later attempt — the only combination that may settle a command as
 * `failed_retryable` instead of `rejected`.
 *
 * Anything unproven is deliberately not retryable: a permanent start fault (a
 * bad ARN, a deleted state machine, input the child's schema rejects) never
 * reaches the provider either, but retrying it is pointless and an operator
 * has to intervene, and an unattributed service exception may not be a start
 * fault at all.
 */
export function isRetryableBillingProviderExecutionStartFault(options: {
  errorName: string;
  errorCause?: string;
}): boolean {
  if (options.errorName === "StepFunctions.ExecutionLimitExceeded") {
    return true;
  }

  if (options.errorName === "StepFunctions.AWSStepFunctionsException") {
    return (
      options.errorCause?.includes(
        PROVIDER_EXECUTION_START_THROTTLE_CAUSE_CODE
      ) === true
    );
  }

  return false;
}

export interface BillingCommandRuntimeStartInputV1 {
  orgId: string;
  commandId: string;
  executionId: string;
}

export interface BillingCommandRuntimeEnvelopeV1 {
  runtimeContract: typeof BILLING_COMMAND_RUNTIME_CONTRACT;
  orgId: string;
  commandId: string;
  commandFamily: BillingCommandFamily;
}

export const BillingCommandRuntimeEnvelopeV1Schema = z.object({
  runtimeContract: z.literal(BILLING_COMMAND_RUNTIME_CONTRACT),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  commandFamily: z.enum(BillingCommandFamilyList),
});

export function isBillingCommandRuntimeEnvelope(
  value: unknown
): value is BillingCommandRuntimeEnvelopeV1 {
  return BillingCommandRuntimeEnvelopeV1Schema.safeParse(value).success;
}

export function assertBillingCommandRuntimeEnvelope(
  value: unknown
): asserts value is BillingCommandRuntimeEnvelopeV1 {
  if (!isBillingCommandRuntimeEnvelope(value)) {
    throw new Error(
      "billing command runtime envelope is missing required contract fields"
    );
  }
}

export interface BillingRuntimeCheckpointV1 {
  phase: BillingRuntimeCheckpointPhase;
  phaseVersion: 1;
  commandFamily: BillingCommandFamily;
  attempt: number;
  planIntentRef?: BillingPlanIntentRefV1;
  policyContext?: {
    requestedTargetSource: string;
    requestedPriceIds?: string[];
    activePriceIds?: string[];
    orderTierSnapshotId?: string;
    catalogProductIds?: string[];
  };
  policyDecision?: {
    policyStatus: "accepted" | "deferred" | "rejected" | "blocked";
    originScenarioId?: string;
    decisionScenarioId?: string;
    executionMode:
      | "immediate_provider_mutation"
      | "deferred_schedule_only"
      | "no_provider_mutation";
    rejectedReason?: string;
    explanationDetails?: Record<string, unknown>;
    deferredBoundaryReason?: BillingScheduledBoundaryReason;
    resolvedCadence?: BillingCadence;
    resolvedPriceIds?: string[];
    requestedEffectiveTiming?: BillingEffectiveTiming;
    appliedPolicies?: string[];
  };
  executionPlan?: {
    planType:
      | "immediate_provider_mutation"
      | "deferred_schedule_only"
      | "no_provider_mutation"
      | "record_only";
    originScenarioId?: string;
    decisionScenarioId?: string;
    billingProvider?: SubscriptionBillingProvider;
    deferredReason?: BillingPlanDeferredReason;
    deferredBoundaryReason?: BillingScheduledBoundaryReason;
    // When a deferred change takes effect, which is what a merchant is shown.
    // A period-end booking fires its wakeup (`dueAt`) ahead of it; absent on
    // plans persisted before the two were split, whose `dueAt` is the boundary.
    boundary?: string;
    dueAt?: string;
    followupCommandType?: BillingScheduledFollowupCommandType;
    internalOutcome?: string;
    agencyLifecycle?: AgencyLifecyclePayload;
    providerLifecycleAction?: ProviderLifecycleAction;
    scheduledWakeup?: BillingScheduledWakeupV1;
    releaseScheduledWakeup?: BillingReleaseScheduledWakeupV1;
    followupPayload?: BillingFollowupPayload;
  };
  targetState?: {
    productIds?: string[];
    priceIds?: string[];
    cadence?: BillingCadence;
    provider?: SubscriptionBillingProvider;
    discountInstructions?: unknown[];
  };
  externalState?: {
    stripeSubscriptionId?: string;
    stripeSubscriptionLinkId?: string;
    stripeScheduleId?: string;
    stripeCustomerId?: string;
    shopifyChargeId?: string;
    expectedWebhookKinds?: string[];
    providerWaitKind?: "customer_billing_ready";
    delayedMarkerId?: string;
    collectionMethod?: SubscriptionCollectionMethod;
  };
  scheduledWakeup?: {
    wakeAt: string;
    reason: BillingScheduledBoundaryReason;
    delayedJobId?: string;
  };
  /**
   * Every delayed-wakeup job this command has booked, by job id. The Dynamo row
   * is written before the SQL transaction that books it commits, so the timer
   * outlives a rolled-back booking; this list is the committed proof that the
   * command actually owns a fired wakeup. It is a set rather than a single ref
   * because one command books more than one boundary (a deferred_schedule_only
   * plan books at internal scheduling and again at timer followup, under
   * different idempotency keys).
   */
  bookedWakeupJobIds?: string[];
  /**
   * Which revision of the wakeup-ownership contract last wrote this checkpoint.
   * Absent means the checkpoint predates `bookedWakeupJobIds`, so an empty list
   * proves nothing and the wakeup consumer must fall back to legacy evidence
   * instead of treating the wakeup as unowned. Delayed jobs are booked up to a
   * year out, so this discriminator has to outlive the rollout.
   */
  wakeupOwnershipVersion?: number;
  /**
   * Set when the version stamp was applied to a checkpoint that predated the
   * contract. Such a command may hold pre-deploy bookings it never recorded, so
   * its booking list is not exhaustive and an unlisted wakeup stays ambiguous
   * rather than being treated as unowned.
   */
  wakeupOwnershipMigratedFromLegacy?: boolean;
  /**
   * Delayed-wakeup jobs this command could not prove or disprove ownership of.
   * The job is deliberately kept, so without this record every poll would take
   * the ownership locks and raise the same alert again for the lifetime of the
   * row. Recording it makes the report happen once and leaves a queryable set
   * for a repair to resolve; an id that later appears in `bookedWakeupJobIds`
   * is proven and wins.
   */
  unresolvedWakeupJobIds?: string[];
  /**
   * Bookings this command committed that a later command deliberately released.
   * The id is dropped from `bookedWakeupJobIds` at the same time, so the record
   * is what tells a released boundary apart from one that was never booked —
   * including on a checkpoint whose booking list is not exhaustive, where the
   * legacy evidence lanes would otherwise still admit the fired timer.
   */
  releasedWakeupBookings?: {
    jobId: string;
    releasedByCommandId: string;
    releasedAt: string;
  }[];
  /**
   * The bookings this command's plan released, one entry per job id, and how
   * each release settled. Written by the releasing command in the transaction
   * that released it: the source's `releasedWakeupBookings` only records
   * releases that took effect, so this is the one place a release that stopped
   * nothing is visible.
   */
  wakeupReleases?: {
    sourceCommandId: string;
    delayedJobId: string;
    outcome: BillingWakeupReleaseOutcome;
  }[];
  projectionRefs?: {
    postMutationSyncRef?: string;
    projectionSyncRef?: string;
    projectionSyncTargets?: string[];
  };
  entitlementApplyRefs?: {
    buildRef?: string;
    applyRef?: string;
    entitlements?: string[];
  };
  providerArtifactRefs?: {
    syncRef?: string;
    stripeSubscriptionId?: string;
    stripeScheduleId?: string;
    shopifyChargeId?: string;
    expectedWebhookKinds?: string[];
  };
  downstreamEffectSummary?: {
    envelopeRef?: string;
    deliveredEffects?: string[];
    pendingRetryEffects?: string[];
    effectDeliveryStatus?: "complete" | "pending_retry" | "retry_exhausted";
  };
  timerRouteOutcome?: {
    route: string;
    scheduledWakeAt?: string;
  };
  conversionCutover?: {
    status: "applied";
    sourceBillingRegime: "legacy_v1" | "stripe_v2";
    targetBillingRegime: "modular_2026";
    appliedAt: string;
  };
  reconciliation?: {
    baselineRef?: string;
    driftSummaryRef?: string;
    driftCategories?: string[];
    repairPlanRef?: string;
    repairRecommendation?: "retry_policy_resolution" | "manual_followup";
  };
  repairSweepSummary?: {
    repairTargetType?: string;
    processedCommandRefs?: string[];
    republishedWakeup?: boolean;
    retryCommandRef?: string;
    manualFollowupRequired?: boolean;
  };
  lastAcceptedSignal?: {
    source: BillingAcceptedSignalSource;
    signalId: string;
    acceptedAt: string;
  };
  lastPersistedAt: string;
}

export const BillingRuntimeCheckpointV1Schema = z.object({
  phase: z.enum(BillingRuntimeCheckpointPhaseList),
  phaseVersion: z.literal(1),
  commandFamily: z.enum(BillingCommandFamilyList),
  attempt: z.number(),
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  policyContext: z
    .object({
      requestedTargetSource: z.string().min(1),
      requestedPriceIds: z.array(z.string().min(1)).optional(),
      activePriceIds: z.array(z.string().min(1)).optional(),
      orderTierSnapshotId: z.string().min(1).optional(),
      catalogProductIds: z.array(z.string().min(1)).optional(),
    })
    .optional(),
  policyDecision: z
    .object({
      policyStatus: z.enum(["accepted", "deferred", "rejected", "blocked"]),
      originScenarioId: z.string().optional(),
      decisionScenarioId: z.string().optional(),
      executionMode: z.enum([
        "immediate_provider_mutation",
        "deferred_schedule_only",
        "no_provider_mutation",
      ]),
      rejectedReason: z.string().optional(),
      explanationDetails: z.record(z.string(), z.unknown()).optional(),
      deferredBoundaryReason: z
        .enum(BillingScheduledBoundaryReasonList)
        .optional(),
      resolvedCadence: z.enum(BillingCadenceList).optional(),
      resolvedPriceIds: z.array(z.string().min(1)).optional(),
      requestedEffectiveTiming: z.enum(BillingEffectiveTimingList).optional(),
      appliedPolicies: z.array(z.string().min(1)).optional(),
    })
    .optional(),
  executionPlan: z
    .object({
      planType: z.enum([
        "immediate_provider_mutation",
        "deferred_schedule_only",
        "no_provider_mutation",
        "record_only",
      ]),
      originScenarioId: z.string().optional(),
      decisionScenarioId: z.string().optional(),
      billingProvider: z.enum(SubscriptionBillingProviderList).optional(),
      deferredReason: BillingPlanDeferredReasonSchema.optional(),
      deferredBoundaryReason: z
        .enum(BillingScheduledBoundaryReasonList)
        .optional(),
      boundary: z.string().optional(),
      dueAt: z.string().optional(),
      followupCommandType: z
        .enum(BillingScheduledFollowupCommandTypeList)
        .optional(),
      internalOutcome: z.string().optional(),
      agencyLifecycle: AgencyLifecyclePayloadSchema.optional(),
      providerLifecycleAction: ProviderLifecycleActionSchema.optional(),
      scheduledWakeup: BillingScheduledWakeupV1Schema.optional(),
      releaseScheduledWakeup: BillingReleaseScheduledWakeupV1Schema.optional(),
      followupPayload: BillingFollowupPayloadSchema.optional(),
    })
    .optional(),
  targetState: z
    .object({
      productIds: z.array(z.string().min(1)).optional(),
      priceIds: z.array(z.string().min(1)).optional(),
      cadence: z.enum(BillingCadenceList).optional(),
      provider: z.enum(SubscriptionBillingProviderList).optional(),
      discountInstructions: z.array(z.unknown()).optional(),
    })
    .optional(),
  externalState: z
    .object({
      stripeSubscriptionId: z.string().optional(),
      stripeSubscriptionLinkId: z.string().optional(),
      stripeScheduleId: z.string().optional(),
      stripeCustomerId: z.string().optional(),
      shopifyChargeId: z.string().optional(),
      expectedWebhookKinds: z.array(z.string().min(1)).optional(),
      providerWaitKind: z.literal("customer_billing_ready").optional(),
      delayedMarkerId: z.string().optional(),
      collectionMethod: z.enum(SubscriptionCollectionMethodList).optional(),
    })
    .optional(),
  scheduledWakeup: z
    .object({
      wakeAt: z.string(),
      reason: z.enum(BillingScheduledBoundaryReasonList),
      delayedJobId: z.string().optional(),
    })
    .optional(),
  bookedWakeupJobIds: z.array(z.string().min(1)).optional(),
  wakeupOwnershipVersion: z.number().int().positive().optional(),
  wakeupOwnershipMigratedFromLegacy: z.boolean().optional(),
  unresolvedWakeupJobIds: z.array(z.string().min(1)).optional(),
  releasedWakeupBookings: z
    .array(
      z.object({
        jobId: z.string().min(1),
        releasedByCommandId: z.string().min(1),
        releasedAt: z.string().min(1),
      })
    )
    .optional(),
  wakeupReleases: z
    .array(
      z.object({
        sourceCommandId: z.string().min(1),
        delayedJobId: z.string().min(1),
        outcome: z.enum(BillingWakeupReleaseOutcomeList),
      })
    )
    .optional(),
  projectionRefs: z
    .object({
      postMutationSyncRef: z.string().optional(),
      projectionSyncRef: z.string().optional(),
      projectionSyncTargets: z.array(z.string().min(1)).optional(),
    })
    .optional(),
  entitlementApplyRefs: z
    .object({
      buildRef: z.string().optional(),
      applyRef: z.string().optional(),
      entitlements: z.array(z.string().min(1)).optional(),
    })
    .optional(),
  providerArtifactRefs: z
    .object({
      syncRef: z.string().optional(),
      stripeSubscriptionId: z.string().optional(),
      stripeScheduleId: z.string().optional(),
      shopifyChargeId: z.string().optional(),
      expectedWebhookKinds: z.array(z.string().min(1)).optional(),
    })
    .optional(),
  downstreamEffectSummary: z
    .object({
      envelopeRef: z.string().optional(),
      deliveredEffects: z.array(z.string().min(1)).optional(),
      pendingRetryEffects: z.array(z.string().min(1)).optional(),
      effectDeliveryStatus: z
        .enum(["complete", "pending_retry", "retry_exhausted"])
        .optional(),
    })
    .optional(),
  timerRouteOutcome: z
    .object({
      route: z.string().min(1),
      scheduledWakeAt: z.string().optional(),
    })
    .optional(),
  conversionCutover: z
    .object({
      status: z.literal("applied"),
      sourceBillingRegime:
        BillingRegimeConversionContextSchema.shape.sourceBillingRegime,
      targetBillingRegime:
        BillingRegimeConversionContextSchema.shape.targetBillingRegime,
      appliedAt: z.string(),
    })
    .optional(),
  reconciliation: z
    .object({
      baselineRef: z.string().optional(),
      driftSummaryRef: z.string().optional(),
      driftCategories: z.array(z.string().min(1)).optional(),
      repairPlanRef: z.string().optional(),
      repairRecommendation: z
        .enum(["retry_policy_resolution", "manual_followup"])
        .optional(),
    })
    .optional(),
  repairSweepSummary: z
    .object({
      repairTargetType: z.string().optional(),
      processedCommandRefs: z.array(z.string().min(1)).optional(),
      republishedWakeup: z.boolean().optional(),
      retryCommandRef: z.string().optional(),
      manualFollowupRequired: z.boolean().optional(),
    })
    .optional(),
  lastAcceptedSignal: z
    .object({
      source: z.enum(BillingAcceptedSignalSourceList),
      signalId: z.string().min(1),
      acceptedAt: z.string(),
    })
    .optional(),
  lastPersistedAt: z.string(),
});

export interface BillingDelayedCommandWakeupPayload {
  commandId: string;
  orgId: string;
  planIntentRef?: BillingPlanIntentRefV1;
  dueAt: string;
  billingProvider: SubscriptionBillingProvider;
  executionPlanVersion: string;
  deferredBoundaryReason: BillingScheduledBoundaryReason;
  idempotencyKey: string;
  followupCommandType: BillingScheduledFollowupCommandType;
  followupPayload?: BillingFollowupPayload;
}

export const BillingDelayedCommandWakeupPayloadSchema = z.object({
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
  dueAt: z.string(),
  billingProvider: z.enum(SubscriptionBillingProviderList),
  executionPlanVersion: z.string().min(1),
  deferredBoundaryReason: z.enum(BillingScheduledBoundaryReasonList),
  idempotencyKey: z.string().min(1),
  followupCommandType: z.enum(BillingScheduledFollowupCommandTypeList),
  followupPayload: BillingFollowupPayloadSchema.optional(),
});

// Distinct from BillingDelayedCommandWakeupPayload because the fired job
// dispatches the existing provider_confirmation `expiry_check` command
// directly through commandFamilyRouter, NOT through scheduled_wakeup
// intent/policy resolution.
export interface BillingProviderConfirmationExpiryDelayedPayload {
  kind: "provider_confirmation_expiry";
  orgId: string;
  commandId: string;
  paymentIntentId: string;
  originalCommandId: string;
  expiresAt: string;
  idempotencyKey: string;
}

export const BillingProviderConfirmationExpiryDelayedPayloadSchema = z.object({
  kind: z.literal("provider_confirmation_expiry"),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  paymentIntentId: z.string().min(1),
  originalCommandId: z.string().min(1),
  expiresAt: z.string(),
  idempotencyKey: z.string().min(1),
});
